import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import employeeForm3 from '../../assets/images/employees/employeeform3.png';
import { apiRequest } from '../../utils/api';

const BankInfoForm = ({ onSubmit, data, enableValidation = false }) => {
  const [banks, setBanks] = useState([]);
  const [isLoadingBanks, setIsLoadingBanks] = useState(false);
  const [bankError, setBankError] = useState('');

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: data,
    mode: 'onSubmit',
  });

  useEffect(() => {
    reset(data || {});
  }, [data, reset]);

  useEffect(() => {
    const currentName = (data?.bankName || '').trim();
    if (!currentName) {
      return;
    }
    const normalized = currentName.toUpperCase();
    if ((getValues('bankName') || '').toUpperCase() !== normalized) {
      setValue('bankName', normalized, { shouldDirty: false, shouldValidate: enableValidation });
    }
  }, [data?.bankName, enableValidation, getValues, setValue]);

  useEffect(() => {
    let isMounted = true;

    const loadBanks = async () => {
      try {
        setIsLoadingBanks(true);
        setBankError('');
        const response = await apiRequest('/api/public/banks', { skipAuthRedirect: true });
        if (!isMounted) {
          return;
        }
        const list = Array.isArray(response.data) ? response.data : [];
        setBanks(list);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setBanks([]);
        setBankError(error.message || 'Failed to load bank list.');
      } finally {
        if (isMounted) {
          setIsLoadingBanks(false);
        }
      }
    };

    loadBanks();

    return () => {
      isMounted = false;
    };
  }, []);

  const watchedIfsc = watch('ifscCode');

  useEffect(() => {
    if (typeof watchedIfsc !== 'string') {
      return;
    }

    const trimmed = watchedIfsc.trim().toUpperCase();
    if (trimmed !== watchedIfsc) {
      setValue('ifscCode', trimmed, { shouldDirty: true, shouldValidate: enableValidation });
      return;
    }

    const sanitized = trimmed.replace(/\s+/g, '');
    if (sanitized !== trimmed) {
      setValue('ifscCode', sanitized, { shouldDirty: true, shouldValidate: enableValidation });
      return;
    }

    if (!sanitized || sanitized.length < 4) {
      return;
    }

    const exactMatch = banks.find(
      (bank) => (bank.ifscCode || '').toUpperCase() === sanitized,
    );
    const prefixMatch = banks.find((bank) =>
      (bank.ifscCode || '').toUpperCase().startsWith(sanitized),
    );
    const matched = exactMatch || prefixMatch;

    if (matched && matched.bankName) {
      const nextValue = (matched.bankName || '').toUpperCase();
      const currentValue = (getValues('bankName') || '').toUpperCase();
      if (currentValue !== nextValue) {
        setValue('bankName', nextValue, {
          shouldDirty: true,
          shouldValidate: enableValidation,
        });
      }
    }
  }, [watchedIfsc, banks, setValue, getValues, enableValidation]);

  const bankOptions = useMemo(() => {
    const unique = new Map();
    const existingName = (data?.bankName || '').trim().toUpperCase();
    if (existingName) {
      unique.set(existingName, { id: `current-${existingName}`, name: existingName });
    }
    banks.forEach((bank) => {
      const name = (bank.bankName || '').toUpperCase();
      if (!name) {
        return;
      }
      if (!unique.has(name)) {
        unique.set(name, { id: bank.id ?? bank.ifscCode ?? name, name });
      }
    });
    return Array.from(unique.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [banks, data?.bankName]);

  const mergeWithExisting = (values) => ({
    ...(typeof data === 'object' && data !== null ? data : {}),
    ...values,
  });

  const getValidationRules = (field) => {
    if (!enableValidation) return {};

    const optionalPatternRule = (regex, message) => ({
      validate: (value) => {
        if (!value) return true;
        return regex.test(value) || message;
      }
    });

    const rules = {
      accountNumber: optionalPatternRule(/^[0-9]{9,18}$/, 'Account number should be 9-18 digits'),
      reAccountNumber: {
        validate: (value) => {
          const accountNumber = getValues('accountNumber');
          const trimmedValue = (value || '').trim();

          if (!trimmedValue && accountNumber) {
            return 'Please re-enter the account number.';
          }

          if (!trimmedValue) {
            return true;
          }

          if (!/^\d+$/.test(trimmedValue)) {
            return 'Re-entered account number must contain digits only';
          }

          if (accountNumber && trimmedValue !== accountNumber.trim()) {
            return 'Account numbers must match';
          }

          return true;
        }
      },
      ifscCode: optionalPatternRule(/^[A-Z]{4}0[A-Z0-9]{6}$/i, 'Enter a valid IFSC code'),
      panCard: optionalPatternRule(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i, 'Enter a valid PAN number'),
      drivingLicense: optionalPatternRule(/^[A-Z0-9-]{5,20}$/i, 'Enter a valid driving license number')
    };

    return rules[field] || {};
  };

  return (
    <form
      onSubmit={handleSubmit((formValues) => onSubmit({ bank: mergeWithExisting(formValues) }))}
      className="d-flex flex-column"
      style={{ minHeight: '80vh' }}
    >
      <div className="row flex-grow-1">
        <div className="col-md-8">
          <div className="row g-4">
            <div className="col-12">
              <div className="form-floating">
                <input
                  type="text"
                  className={`form-control ${errors.accountNumber ? 'is-invalid' : ''}`}
                  id="accountNumber"
                  placeholder="Enter Bank Account Number"
                  {...register('accountNumber', getValidationRules('accountNumber'))}
                />
                <label htmlFor="accountNumber">Enter Bank Account Number</label>
                {errors.accountNumber && <div className="invalid-feedback">{errors.accountNumber.message}</div>}
              </div>
            </div>

            <div className="col-12">
              <div className="form-floating">
                <input
                  type="text"
                  className={`form-control ${errors.reAccountNumber ? 'is-invalid' : ''}`}
                  id="reAccountNumber"
                  placeholder="Re Enter Bank Account Number"
                  {...register('reAccountNumber', getValidationRules('reAccountNumber'))}
                />
                <label htmlFor="reAccountNumber">Re Enter Bank Account Number</label>
                {errors.reAccountNumber && <div className="invalid-feedback">{errors.reAccountNumber.message}</div>}
              </div>
            </div>

            <div className="col-12">
              <div className="form-floating">
                <input
                  type="text"
                  className={`form-control ${errors.ifscCode ? 'is-invalid' : ''}`}
                  id="ifscCode"
                  placeholder="Enter IFSC Code"
                  {...register('ifscCode', getValidationRules('ifscCode'))}
                />
                <label htmlFor="ifscCode">Enter IFSC Code</label>
                {errors.ifscCode && <div className="invalid-feedback">{errors.ifscCode.message}</div>}
              </div>
            </div>

            <div className="col-12">
              <div className="form-floating">
                <select
                  className={`form-select ${errors.bankName ? 'is-invalid' : ''}`}
                  id="bankName"
                  {...register('bankName', getValidationRules('bankName'))}
                >
                  <option value="">
                    {isLoadingBanks ? 'Loading banks...' : 'Select Bank Name'}
                  </option>
                  {bankOptions.map((bank) => (
                    <option key={bank.id} value={bank.name}>
                      {bank.name}
                    </option>
                  ))}
                </select>
                <label htmlFor="bankName">Select Bank Name</label>
                {bankError && !errors.bankName && (
                  <div className="text-danger small mt-2">{bankError}</div>
                )}
                {errors.bankName && <div className="invalid-feedback">{errors.bankName.message}</div>}
              </div>
            </div>

            <div className="col-12">
              <div className="form-floating">
                <input
                  type="text"
                  className={`form-control ${errors.panCard ? 'is-invalid' : ''}`}
                  id="panCard"
                  placeholder="Enter PAN Card Number"
                  {...register('panCard', getValidationRules('panCard'))}
                />
                <label htmlFor="panCard">Enter PAN Card Number</label>
                {errors.panCard && <div className="invalid-feedback">{errors.panCard.message}</div>}
              </div>
            </div>

            <div className="col-12">
              <div className="form-floating">
                <input
                  type="text"
                  className={`form-control ${errors.drivingLicense ? 'is-invalid' : ''}`}
                  id="drivingLicense"
                  placeholder="Enter Driving License Number"
                  {...register('drivingLicense', getValidationRules('drivingLicense'))}
                />
                <label htmlFor="drivingLicense">Enter Driving License Number</label>
                {errors.drivingLicense && <div className="invalid-feedback">{errors.drivingLicense.message}</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="position-fixed bottom-25 end-0 d-none d-md-block" style={{ maxWidth: '400px', zIndex: 1 }}>
            <img 
              src={employeeForm3} 
              alt="Bank Information" 
              className="img-fluid" 
              style={{ maxHeight: '50vh' }} 
            />
          </div>
        </div>
      </div>

      <div className="mt-auto pt-4 d-flex justify-content-between">
        <button
          type="button"
          onClick={() => onSubmit({ bank: mergeWithExisting(getValues()), goBack: true })}
          className="btn btn-outline-secondary px-4"
        >
          Back
        </button>
        <button type="submit" className="btn btn-danger px-4">
          Next
        </button>
      </div>
    </form>
  );
};

export default BankInfoForm;
